import { Reference, useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import ActionIconButton from "@components/buttons/ActionIconButton";
import CircleButton from "@components/buttons/CircleButton";
import ClientAvatar from "@components/ClientAvatar";
import DateStamp from "@components/dates/DateStamp";
import ExpertAvatar from "@components/ExpertAvatar";
import SearchInput from "@components/fieldsNative/SearchInput";
import { ChatsLoader } from "@components/loaders/MessagesLoader";
import { MessageSearch } from "@components/messaging/Messages";
import styles from "@components/messaging/Messages.module.css";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import TextWithIcon from "@components/text/TextWithIcon";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMe from "@hooks/useMe";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useTimeAgo from "@hooks/useTimeAgo";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ChatCreateDocument,
  ChatCreateMutation,
  ChatCreateMutationVariables,
  ChatFragment,
  ChatFragmentDoc,
  GetJourneyForMessagesQuery,
  GetJourneyMessagesDocument,
  GetJourneyMessagesQuery,
  GetJourneyMessagesQueryVariables,
  UserTypeEnum
} from "@typing/Generated";
import { getFileImage } from "@utils/fileUtils";
import { imageUrl } from "@utils/imageUtils";
import { getChatUsers, sortByTimestamp } from "@utils/modelUtils";
import { escapeRegularExpressionText } from "@utils/stringUtils";

type Journey = GetJourneyForMessagesQuery["journey"];

type JourneyMessage = NonNullable<
  NonNullable<NonNullable<GetJourneyMessagesQuery["journeyMessages"]["edges"]>[number]>["node"]
>;

type Props = {
  handleMessageSearchFind: (chatId: string, messageId: string, searchTerm: string) => void;
  isFab?: boolean;
  journey: GetJourneyForMessagesQuery["journey"];
  messageSearch: MessageSearch;
  selectedChat?: ChatFragment | undefined;
  setEditingChat: (editingChat: boolean) => void;
  setMessageSearch: (messageSearch: MessageSearch) => void;
  setSelectedChat: (chat: ChatFragment | undefined) => void;
};

type FormData = {
  search?: string | null;
};

type ChatProps = {
  chat: ChatFragment;
  handleChatSelect: (chat: ChatFragment) => void;
  journey: Journey;
  selectedChat?: ChatFragment | undefined;
};

type ChatUserLabelProps = {
  chat: ChatFragment;
  journey: GetJourneyForMessagesQuery["journey"];
};

const ChatUserLabels = ({ chat, journey }: ChatUserLabelProps) => {
  const me = useMe();
  const chatUsers = getChatUsers(me, chat, journey);
  const firstThreeUsers = chatUsers.slice(0, 3);

  return (
    <div className={styles.controlChatUsers}>
      <div className={styles.controlsAvatarsSmall}>
        {firstThreeUsers.map((user, index) => {
          if (index === 2 && chatUsers.length > 3) {
            return (
              <div className={styles.controlsAvatarContainer} key={user.id}>
                <div
                  className={styles.andXOthers}
                  style={{
                    left: (firstThreeUsers.length - index) * 22,
                    top: 30,
                    zIndex: 100000
                  }}
                >
                  <FormattedMessage id="dictionary.punctuation.plus" />
                  {chatUsers.length - 2}
                </div>
              </div>
            );
          }

          return (
            <div className={styles.controlsAvatarContainer} key={user.id}>
              <div
                className={styles.controlsAvatarSmall}
                style={{
                  left: (firstThreeUsers.length - index) * 22,
                  zIndex: firstThreeUsers.length + 1 - index
                }}
              >
                {user.userType === UserTypeEnum.EXPERT && "expert" in user.avatarProps ? (
                  <ExpertAvatar {...user.avatarProps} />
                ) : "firstName" in user.avatarProps ? (
                  <ClientAvatar {...user.avatarProps} clientId={user.id} journeyId={journey.id} />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <BodyCopy bold color="gray-500">
        {chatUsers.length > 3
          ? firstThreeUsers
              .slice(0, 2)
              .map(user => user.name)
              .join(", ")
          : firstThreeUsers.map(user => user.name).join(", ")}
        {chatUsers.length > 3 && (
          <>
            <Nbsp />
            <FormattedMessage id="pages.messages.andXOthers" values={{ count: chatUsers.length - 2 }} />
          </>
        )}
      </BodyCopy>
    </div>
  );
};

const Chat = ({ chat, handleChatSelect, journey, selectedChat }: ChatProps) => {
  const timeAgo = useTimeAgo(chat.lastMessage?.createdAt);

  return (
    <div className={styles.controlsChatBorder} data-test="chat">
      <div
        className={`${styles.controlsChat} ${selectedChat?.id === chat.id ? styles.controlsChatSelected : ""}`}
        onClick={() => handleChatSelect(chat)}
      >
        <div className={styles.controlsChatHeader}>
          <ChatUserLabels chat={chat} journey={journey} />
          {chat.unreadMessageCount > 0 ? (
            <div className={styles.controlsUnreadMessages}>
              <BodyCopy color="white" size="small">
                {chat.unreadMessageCount}
              </BodyCopy>
            </div>
          ) : chat.lastMessage ? (
            <div className={styles.controlsTimeAgo}>
              <BodyCopy color="gray-500">{timeAgo}</BodyCopy>
            </div>
          ) : null}
        </div>
        <div className={styles.controlsLastMessage}>
          <BodyCopy color="gray-500">{chat.lastMessage?.bodyPlainText}</BodyCopy>
        </div>
      </div>
    </div>
  );
};

const MessagesControls = ({
  handleMessageSearchFind,
  isFab = false,
  journey,
  messageSearch,
  selectedChat,
  setEditingChat,
  setMessageSearch,
  setSelectedChat
}: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();

  const [createChat] = useMutationWithValidation<ChatCreateMutation, ChatCreateMutationVariables>(
    ChatCreateDocument,
    "chat",
    {
      update: (store, { data }) => {
        if (data?.chatCreate?.chat) {
          store.modify({
            fields: {
              chats(cachedChatRefs: readonly Reference[]) {
                const newChatRef = store.writeFragment({
                  data: data.chatCreate?.chat,
                  fragment: ChatFragmentDoc,
                  fragmentName: "Chat"
                });
                if (newChatRef && data.chatCreate?.chat) {
                  return [...cachedChatRefs, newChatRef];
                }
                return [...cachedChatRefs];
              }
            },
            id: store.identify({ ...journey })
          });
        }
      }
    }
  );

  const handleChatCreate = () => {
    if (journey.chats.length > 0) {
      setSelectedChat(sortByTimestamp(journey.chats, chat => chat.createdAt).reverse()[0]);
      setEditingChat(true);
    } else if (myClient) {
      const clientIds = Array.from(new Set([myClient.id, journey.owner.id]));

      createChat({
        clientIds,
        journeyId: journey.id
      }).then(res => {
        if (res?.chatCreate?.chat) {
          setSelectedChat(res.chatCreate.chat);
          setEditingChat(true);
        }
      });
    }
  };

  const [search, setSearch] = useState<string | null | undefined>(null);

  const [query, setQuery] = useQueryParams({
    search: withDefault({ ...StringParam, urlName: "messageSearch" }, search)
  });

  const defaultValues: FormData = query;
  const messagesOpener = useGlobalOpener(OpenerTypeEnum.MESSAGES);

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const searchValue = form.watch("search");

  useEffect(() => {
    if (!search) {
      setMessageSearch({ chatId: null, found: null, messageId: null, searchTerm: null });
    }
  }, [search, setMessageSearch]);

  useEffect(() => {
    if (search !== searchValue) {
      setQuery({ search: searchValue });
      setSearch(searchValue);
    }
  }, [setQuery, search, searchValue]);

  const handleChatSelect = (chat: ChatFragment) => {
    setSelectedChat(chat);
    setEditingChat(false);
  };

  const [getJourneyMessages, getJourneyMessagesQuery] = useLazyQuery<
    GetJourneyMessagesQuery,
    GetJourneyMessagesQueryVariables
  >(GetJourneyMessagesDocument, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (search) {
      getJourneyMessages({
        variables: {
          id: journey.id,
          search: search ?? ""
        }
      });
    }
  }, [getJourneyMessages, journey.id, search]);

  const journeyMessages = useNodesFromConnection(getJourneyMessagesQuery.data?.journeyMessages);

  const searchResults = useMemo(() => {
    if (!search) return null;

    const fileResources = journeyMessages
      .map(message => message.fileResources.flat())
      .flat()
      .filter(fr => fr.title?.toLowerCase().includes(search.toLowerCase()))
      .map(fr => ({
        fr,
        message: journeyMessages.find(message => message.fileResources.includes(fr)) as JourneyMessage
      }));

    return {
      fileResources,
      messages: journeyMessages
        .filter(message => message.bodyPlainText?.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 3)
    };
  }, [journeyMessages, search]);

  function extractSubstring(string: string, searchTerm: string) {
    // Convert both string and searchTerm to lowercase
    const lowercasedString = string.toLowerCase();
    const lowercasedSearchTerm = searchTerm.toLowerCase();

    // Find the index of the search term in the lowercase string
    const index = lowercasedString.indexOf(lowercasedSearchTerm);

    if (index !== -1) {
      // Determine the starting and ending indices for the substring
      const start = Math.max(0, index - 40);
      const end = Math.min(string.length, index + searchTerm.length + 40);

      // Extract the substring from the original string
      const substring = string.substring(start, end);
      return substring;
    } else {
      return null;
    }
  }

  const highlightText = (text: string, searchTerm: string) => {
    const regex = new RegExp(`(${escapeRegularExpressionText(searchTerm)})`, "gi");
    const parts = text.split(regex);
    return parts.map(part => (
      <span className={part.toLowerCase() === searchTerm.toLowerCase() ? styles.highlight : ""} key={part}>
        {part}
      </span>
    ));
  };

  return (
    <div className={isFab ? styles.controlsFab : styles.controls}>
      <div className={styles.controlsHeader}>
        <Heading2 bold={!isFab}>
          <FormattedMessage id="pages.messages.title" />
        </Heading2>
        <div className={styles.newChat}>
          {isFab ? (
            <div className={styles.fabControls}>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.new"
                color="gray-500"
                icon="penToSquare"
                onClick={handleChatCreate}
                size="medium"
              />
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.close"
                color="gray-500"
                icon="times"
                onClick={messagesOpener.close}
                size="medium"
              />
            </div>
          ) : (
            <CircleButton
              aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.new" })}
              color="primary-300"
              icon="penToSquare"
              onClick={handleChatCreate}
            />
          )}
        </div>
      </div>
      <div className={styles.controlsSearch}>
        <SearchInput
          error={form.formState.errors.search?.message}
          initialValue={defaultValues.search}
          name="search"
          setValue={v => form.setValue("search", v)}
        />
      </div>
      <div className={styles.controlsAllThread}>
        <TextWithIcon color="primary" icon="messages" text={intl.formatMessage({ id: "pages.messages.allThreads" })} />
      </div>
      <div className={styles.controlsChats}>
        {searchResults ? (
          <div>
            {getJourneyMessagesQuery.loading ? (
              <ChatsLoader />
            ) : (
              searchResults.messages.map(message => (
                <div
                  className={styles.controlsSearchResult}
                  key={message.id}
                  onClick={() => {
                    if (messageSearch.found === null) {
                      handleMessageSearchFind(message.chat.id, message.id, search ?? "");
                    }
                  }}
                >
                  <div className={styles.controlsSearchResultHeader}>
                    <div className={styles.fullWidth}>
                      <ChatUserLabels
                        chat={journey.chats.find(chat => chat.id === message.chat.id) as ChatFragment}
                        journey={journey}
                      />
                    </div>
                    <div className={styles.noWrap + " " + styles.tinySpacing}>
                      <BodyCopy color="gray-500">
                        <DateStamp mmddyyyy value={message.createdAt} />
                      </BodyCopy>
                    </div>
                    <Icon color="primary" icon="chevronRight" />
                  </div>
                  <div className={styles.controlsLastMessage}>
                    <BodyCopy color="gray-500">
                      {highlightText(extractSubstring(message.bodyPlainText ?? "", search ?? "") ?? "", search ?? "")}
                    </BodyCopy>
                  </div>
                </div>
              ))
            )}
            {searchResults.fileResources.length > 0 && (
              <div>
                <div className={styles.controlsAllThread}>
                  <TextWithIcon
                    color="primary"
                    icon="paperclip"
                    text={intl.formatMessage({ id: "pages.messages.attachments" })}
                  />
                </div>
                {searchResults.fileResources.map(fileResource => (
                  <div
                    className={styles.controlsSearchResult}
                    key={fileResource.fr.id}
                    onClick={() => {
                      if (messageSearch.found === null) {
                        handleMessageSearchFind(fileResource.message.chat.id, fileResource.message.id, search ?? "");
                      }
                    }}
                  >
                    <div className={styles.controlsSearchResultHeader}>
                      <div className={styles.fullWidth} />
                      <div className={styles.noWrap + " " + styles.tinySpacing}>
                        <BodyCopy color="gray-500">
                          <DateStamp mmddyyyy value={fileResource.message.createdAt} />
                        </BodyCopy>
                      </div>
                      {fileResource.message.chat.id === messageSearch.chatId && messageSearch.found === false ? (
                        <Icon color="primary" icon="spinner" />
                      ) : (
                        <Icon color="primary" icon="chevronRight" />
                      )}
                    </div>
                    <div className={styles.controlFile}>
                      <img
                        aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
                        className={styles.controlsLargeFileImg}
                        src={imageUrl(getFileImage(fileResource.fr))}
                      />
                      <div className={styles.controlsLastMessage}>
                        <BodyCopy color="gray-500">{highlightText(fileResource.fr.title ?? "", search ?? "")}</BodyCopy>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {sortByTimestamp(journey.chats, chat => chat.lastMessage?.createdAt).map(chat => (
              <Chat
                chat={chat}
                handleChatSelect={handleChatSelect}
                journey={journey}
                key={chat.id}
                selectedChat={selectedChat}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesControls;
